import React from "react";
import styles from "../PreviewV3.module.css";

export default function RiskMatrixSection({ swmsData, displayName, sectionNumber, targetId }) {
  const meta = (swmsData && swmsData.meta) || {};
  const likelihoods = meta.likelihoods || [];
  const consequences = meta.consequences || [];
  const riskRatings = meta.risk_ratings || {};
  const risks = riskRatings.risk_rating_items || [];
  const hierarchy = meta.hierarchy_of_controls || [];

  return (
    <div className={styles.PreviewSectionBlock} id={targetId} style={{ scrollMarginTop: "140px" }}>
      <div className={styles.PreviewSectionHead}>
        <span className={styles.PreviewSectionNum}>{sectionNumber}</span>
        {displayName}
      </div>
      <div className={styles.PreviewSectionBody}>
        <div className={styles.PreviewRiskMatrixBlock}>

          <div className={styles.PreviewRiskMatrixStepsRow}>

            <div className={styles.PreviewRiskMatrixStep}>
              <div className={styles.PreviewRiskMatrixStepHeading}>
                <span className={styles.PreviewRiskMatrixStepHeadingNumber}>1</span>
                Likelihood
              </div>
              <div className={styles.PreviewRiskMatrixStepBlock}>
                <table className={styles.PreviewRiskMatrixStepTable}>
                  <thead>
                    <tr>
                      <th>Likelihood</th>
                      <th>Value</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {likelihoods.map((likelihood, index) => {
                      const item = likelihood || {};
                      return (
                        <tr key={index}>
                          <td className={styles.PreviewRiskMatrixStepTableTdName}>{item.name}</td>
                          <td className={styles.PreviewRiskMatrixStepTableTdValue}>{item.value}</td>
                          <td className={styles.PreviewRiskMatrixStepTableTdDescription}>{item.description}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.PreviewRiskMatrixStep}>
              <div className={styles.PreviewRiskMatrixStepHeading}>
                <span className={styles.PreviewRiskMatrixStepHeadingNumber}>2</span>
                Consequence
              </div>
              <div className={styles.PreviewRiskMatrixStepBlock}>
                <table className={styles.PreviewRiskMatrixStepTable}>
                  <thead>
                    <tr>
                      <th>Consequence</th>
                      <th>Value</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consequences.map((consequence, index) => {
                      const item = consequence || {};
                      return (
                        <tr key={index}>
                          <td className={styles.PreviewRiskMatrixStepTableTdName}>{item.name}</td>
                          <td className={styles.PreviewRiskMatrixStepTableTdValue}>{item.value}</td>
                          <td className={styles.PreviewRiskMatrixStepTableTdDescription}>{item.description}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </div>

          <div className={styles.PreviewRiskMatrixStep}>
            <div className={styles.PreviewRiskMatrixStepHeading}>
              <span className={styles.PreviewRiskMatrixStepHeadingNumber}>3</span>
              Risk Matrix &middot; Likelihood &times; Consequence
            </div>
            <div className={styles.PreviewRiskMatrixStepBlock}>
              <table className={styles.PreviewRiskMatrixTable}>
                <colgroup>
                  <col style={{ width: "140px" }} />
                  {consequences.map((_, index) => (
                    <col key={index} style={{ width: `calc((100% - 140px) / ${consequences.length})` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2} className={styles.RiskMatrixLikelihoodHeading}>Likelihood</th>
                    <th colSpan={consequences.length} className={styles.RiskMatrixConsequenceHeading}>Consequence</th>
                  </tr>
                  <tr>
                    {consequences.map((consequence, index) => {
                      const item = consequence || {};
                      return (
                        <th key={index} className={styles.RiskMatrixConsequenceTh}>{item.name} ({item.value})</th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {likelihoods.map((likelihood) => (
                    <tr key={likelihood.id || likelihood.value}>
                      <td className={styles.RiskMatrixLikelihoodTd}>
                        {likelihood.name} ({likelihood.value})
                      </td>
                      {consequences.map((consequence) => {
                        const score = Number(likelihood.value) * Number(consequence.value);
                        const associatedRisk = risks.find(
                          (risk) =>
                            Number(risk.risk_rating_high_value) >= score &&
                            Number(risk.risk_rating_low_value) <= score
                        );
                        return (
                          <td
                            key={`${likelihood.id || likelihood.value}-${consequence.id || consequence.value}`}
                            className={styles.RiskMatrixRiskValueCell}
                            style={{
                              backgroundColor: associatedRisk && associatedRisk.risk_colours ? associatedRisk.risk_colours : "#fff",
                              color: associatedRisk && associatedRisk.risk_colours && "#7F1D1D" === associatedRisk.risk_colours ? "#fff" : "#1E293B"
                            }}
                          >
                            {score}{associatedRisk && associatedRisk.risk_rating_label ? ` (${associatedRisk.risk_rating_label})` : ""}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.RiskMatrixBlock}>
            <div className={styles.RiskMatrixBlockHeading}>
              Risk Rating Guide
            </div>
            <div className={styles.RiskRatingGrid}>
              {risks.map((risk, index) => (
                <div key={index} className={styles.RiskRatingGridItem} style={{ backgroundColor: risk.risk_colours }}>
                  <div className={styles.RiskRatingGridItemHeadingRow}>
                    <span className={styles.RiskRatingGridItemHeadingText}>{risk.risk_ratings}</span>
                    <span className={styles.RiskRatingGridItemHeadingSubText}>{risk.risk_rating_low_value} - {risk.risk_rating_high_value}</span>
                  </div>
                  <div className={styles.RiskRatingGridItemDescription}>{risk.risk_rating_description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.RiskMatrixBlock}>
            <div className={styles.RiskMatrixBlockHeading}>
              Hierarchy of Control
              </div>
              <div className={styles.HierarchyGrid}>
                {hierarchy.map((item, index) => (
                  <div key={index} className={styles.HierarchyGridItem}>
                    <div className={styles.HierarchyGridItemHeading}>
                      {index+1} &mdash; {item.name}
                    </div>
                    <div className={styles.HierarchyGridItemDescription}>
                      {item.description}
                    </div>
                  </div>
                  ))}
              </div>
          </div>
        </div>
      </div>
    </div>
  )
}